import { useStore } from "@store/index";
import { useFilterStore } from "@store/filterStore";
import type { FC } from "react";
import { memo, useCallback, useEffect } from "react";
import { CommonToolbarFilter } from "@components/commerce/product-listing/filters/toolbar/item/common/CommonToolbarFilter";
import { useRefinementList } from "react-instantsearch";
import { SizeToolbarFilter } from "./size/SizeToolbarFilter";
import { FilterTypes } from "@lib/constants/filtersConstants";
import { PriceToolbarFilter } from "@components/commerce/product-listing/filters/toolbar/item/price/PriceToolbarFilter";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import type { IPartFilterFields } from "@contentful-api/types/contentful";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import dynamic from "next/dynamic";
import { debounce } from "instantsearch.js/es/lib/utils";

const ColorToolbarFilter = dynamic(() => import("./color/ColorToolbarFilter"), { ssr: false });

export interface ToolbarFilterItemProps extends Partial<IPartFilterFields> {
    items: RefinementListItem[];
    refine: (refinement: string) => void;
    initialFilters: {
        [p: string]: Partial<RefinementListItem>[];
    };
    isLoading?: boolean;
}

const ToolbarFilterItem = memo(
    ({
        filterName,
        filterType,
        isToolbar,
        items,
        refine,
        initialFilters,
    }: ToolbarFilterItemProps) => {
        if (initialFilters?.[filterName]?.length <= 1 || !initialFilters?.[filterName]) {
            return <></>;
        }

        switch (filterType) {
            case FilterTypes.price:
                return (
                    <PriceToolbarFilter
                        items={items}
                        initialFilters={initialFilters[filterName]}
                        refine={refine}
                        filterName={filterName}
                        isToolbar={isToolbar}
                    />
                );
            case FilterTypes.color:
                return (
                    <ColorToolbarFilter
                        items={items}
                        initialFilters={initialFilters[filterName]}
                        refine={refine}
                        filterName={filterName}
                        isToolbar={isToolbar}
                    />
                );
            case FilterTypes.size:
                return <SizeToolbarFilter filterName={filterName} isToolbar={isToolbar} />;
            default:
                return (
                    <CommonToolbarFilter
                        items={items}
                        initialFilters={initialFilters[filterName]}
                        refine={refine}
                        filterName={filterName}
                        isToolbar={isToolbar}
                    />
                );
        }
    }
);

ToolbarFilterItem.displayName = "ToolbarFilterItem";

interface IToolbarFilterItemContainerProps extends IPartFilterFields {}

const ToolbarFilterItemContainer: FC<IToolbarFilterItemContainerProps> = ({
    filterName,
    facetName,
    filterType,
    isToolbar,
}) => {
    // Static data from main store
    const initialFilters = useStore((state) => state.data.products?.initialFilters);
    const setLastLoadedPage = useStore((state) => state.setLastLoadedPage);

    // Dynamic filter store actions (no state subscriptions to avoid rerenders)
    const setFilterItems = useFilterStore((state) => state.setFilterItems);
    const facets = useFilterStore((state) => state.facets);

    const { items, refine } = useRefinementList({
        attribute: facetName || "na",
        operator: "or",
        sortBy: ["name"],
        limit: 100,
    });

    // Update store with current facet data whenever items or refine function changes
    useEffect(() => {
        const newState = facets;
        newState[filterName] = { items, refine };
        setFilterItems({ ...newState });
    }, [items]);

    const applyRefinement = useCallback(
        debounce((item: string) => {
            // Analytics (fire immediately, no timer needed)
            AnalyticsClient.listFilterApplied(filterName, item, facetName);

            // UI actions
            scrollTo({ top: 0 });
            setLastLoadedPage(1);
            refine(item);
        }, 0),
        [refine, setLastLoadedPage, filterName, facetName]
    );

    return (
        <ToolbarFilterItem
            isToolbar={isToolbar}
            filterType={filterType}
            filterName={filterName}
            items={items}
            refine={applyRefinement}
            initialFilters={initialFilters}
        />
    );
};

export { ToolbarFilterItemContainer as ToolbarFilterItem };

export default ToolbarFilterItemContainer;
